@if (item) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" (click)="close()">
    <div class="bg-white rounded-3xl shadow-xl border border-stone-100 p-6 w-full max-w-sm mx-4" (click)="$event.stopPropagation()">

      <!-- Header -->
      <div class="flex items-center justify-between mb-5">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            <span class="material-icons text-primary-500">card_membership</span>
          </div>
          <div>
            <p class="font-semibold text-stone-800 text-sm">{{ title }}</p>
            <p class="text-xs text-stone-400 mt-0.5">{{ fullName }}</p>
          </div>
        </div>
        <button type="button" (click)="close()" class="w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 hover:bg-stone-100 transition-colors">
          <span class="material-icons text-base">close</span>
        </button>
      </div>

      <!-- Success state (after generation or already generated) -->
      @if (generatedNumber() || item.demande_status === 'LICENCE_GENERATED') {
        <div class="flex flex-col items-center gap-4 py-4">
          <div class="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center">
            <span class="material-icons text-emerald-500 text-2xl">check_circle</span>
          </div>
          <div class="text-center">
            <p class="font-semibold text-stone-800 text-sm">Licence générée</p>
            <p class="text-xs text-stone-400 mt-1">Numéro de licence</p>
            <p class="font-mono font-bold text-primary-600 text-base mt-1 bg-primary-50 px-3 py-1.5 rounded-xl border border-primary-100">{{ generatedNumber() || item.licence_number || '—' }}</p>
          </div>
          <!-- View / Download buttons -->
          <div class="flex gap-2 w-full">
            <button type="button" (click)="viewPdf()" [disabled]="isViewing() || isRegenerating()"
              class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors disabled:opacity-60">
              @if (isViewing()) {
                <div class="w-3.5 h-3.5 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                Chargement…
              } @else {
                <span class="material-icons text-base">visibility</span>
                Voir le PDF
              }
            </button>
            <button type="button" (click)="downloadPdf()" [disabled]="isDownloading() || isRegenerating()"
              class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-60">
              @if (isDownloading()) {
                <div class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Téléchargement…
              } @else {
                <span class="material-icons text-base">download</span>
                Télécharger
              }
            </button>
          </div>

          <!-- Regenerate PDF in file service (secondary action) -->
          @if (isRegenerating() || isPdfPending()) {
            <div class="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-stone-400 bg-stone-50 border border-stone-200 rounded-xl">
              <div class="w-3 h-3 border-2 border-stone-300 border-t-stone-500 rounded-full animate-spin"></div>
              Régénération du fichier en cours…
            </div>
          } @else {
            <button type="button" (click)="regeneratePdf()" [disabled]="isDownloading()"
              class="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-stone-500 bg-stone-50 border border-stone-200 rounded-xl hover:bg-stone-100 transition-colors disabled:opacity-40">
              <span class="material-icons text-sm">refresh</span>
              Régénérer la licence
            </button>
          }

          @if (regenerateSuccess()) {
            <div class="flex items-center gap-2 p-3 rounded-xl bg-green-50 border border-green-100">
              <span class="material-icons text-green-500 text-base">check_circle</span>
              <p class="text-xs text-green-700">PDF régénéré avec succès sur le serveur.</p>
            </div>
          }

          @if (errorMessage()) {
            <div class="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
              <span class="material-icons text-red-500 text-base mt-0.5">error_outline</span>
              <p class="text-xs text-red-700">{{ errorMessage() }}</p>
            </div>
          }

          <button type="button" (click)="close()"
            class="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors">
            Fermer
          </button>
        </div>
      } @else {

        <!-- Avatar + Name -->
        <div class="flex items-center gap-4 mb-5 p-4 rounded-2xl bg-stone-50 border border-stone-100">
          <div class="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span class="text-sm font-bold text-primary-700">{{ initials }}</span>
          </div>
          <div class="min-w-0">
            <p class="font-semibold text-stone-800 text-sm">{{ item.data_last_name || item.data_club_name || item.club_name || '—' }}
              <span class="font-normal text-stone-500">{{ item.data_first_name || '' }}</span>
            </p>
            @if (item.data_last_name_ara) {
              <p class="text-xs text-stone-400 mt-0.5 text-right" dir="rtl">{{ item.data_last_name_ara }} {{ item.data_first_name_ara }}</p>
            }
            <div class="mt-1.5 flex items-center gap-2">
              <span
                class="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full"
                [style.color]="demandeStatusColor(item.demande_status).text"
                [style.backgroundColor]="demandeStatusColor(item.demande_status).bg"
              >
                <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" [style.backgroundColor]="demandeStatusColor(item.demande_status).text"></span>
                {{ item.demande_status_label || (item.demande_status | demandeLabel) }}
              </span>
              @if (item.demande_type) {
                <span class="text-[10px] font-bold text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{{ item.demande_type_label || (item.demande_type | demandeLabel) }}</span>
              }
            </div>
          </div>
        </div>

        <!-- Not actionable warning -->
        @if (!isActionable) {
          <div class="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100 mb-4">
            <span class="material-icons text-amber-500 text-base mt-0.5">info</span>
            <p class="text-xs text-amber-700">La demande doit être <strong>validée par la fédération</strong> avant de générer une licence.</p>
          </div>
        }

        <!-- Info Grid -->
        <div class="space-y-0 mb-5">
          @if (item.data_identity_number) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">CNIE</span>
              <span class="text-xs font-mono font-medium text-stone-700">{{ item.data_identity_number }}</span>
            </div>
          }
          @if (item.data_gender) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">Genre</span>
              @if (item.data_gender === 'F') {
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-pink-50 text-pink-600 border border-pink-100">Féminin</span>
              } @else {
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-50 text-sky-600 border border-sky-100">Masculin</span>
              }
            </div>
          }
          @if (item.data_birthday) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">Date de naissance</span>
              <span class="text-xs font-medium text-stone-700">{{ item.data_birthday | date:'dd/MM/yyyy' }}</span>
            </div>
          }
          @if (item.data_phone) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">Téléphone</span>
              <span class="text-xs font-mono text-stone-700">{{ item.data_phone }}</span>
            </div>
          }
          @if (item.person_role_label || item.data_person_role) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">Rôle</span>
              <span class="text-xs font-medium text-stone-700">{{ item.person_role_label || item.data_person_role }}</span>
            </div>
          }
          @if (item.club_name || item.data_club_adress_city) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">{{ entityType === 'club' ? 'Ville' : 'Club' }}</span>
              <span class="text-xs font-medium text-stone-700">{{ entityType === 'club' ? (item.data_club_adress_city || '—') : (item.club_name || '—') }}</span>
            </div>
          }
          @if (item.sportseason_short_label || item.sportseason_code) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">Saison</span>
              <span class="text-xs font-medium text-stone-700">{{ item.sportseason_short_label || item.sportseason_code }}</span>
            </div>
          }
          @if (item.licenceperiod_name) {
            <div class="flex items-center justify-between py-2 border-b border-stone-50">
              <span class="text-xs text-stone-400">Période</span>
              <span class="text-xs font-medium text-stone-700">{{ item.licenceperiod_name }}</span>
            </div>
          }
          @if (item.demande_fed_seq) {
            <div class="flex items-center justify-between py-2">
              <span class="text-xs text-stone-400">Référence</span>
              <span class="text-xs font-mono bg-stone-100 px-2 py-0.5 rounded text-stone-600">{{ item.demande_fed_seq }}</span>
            </div>
          }
        </div>

        <!-- Error -->
        @if (errorMessage()) {
          <div class="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100 mb-4">
            <span class="material-icons text-red-500 text-base mt-0.5">error_outline</span>
            <p class="text-xs text-red-700">{{ errorMessage() }}</p>
          </div>
        }

        <!-- Actions -->
        <div class="flex gap-2 justify-end">
          <button type="button" (click)="close()" [disabled]="isSubmitting()"
            class="px-4 py-2 text-sm font-medium text-stone-600 border border-stone-200 rounded-xl hover:bg-stone-50 transition-colors disabled:opacity-40">
            Annuler
          </button>
          <button type="button" (click)="submit()" [disabled]="isSubmitting() || !isActionable"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            @if (isSubmitting()) {
              <div class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Génération…
            } @else {
              <span class="material-icons text-base">card_membership</span>
              Générer
            }
          </button>
        </div>
      }

    </div>
  </div>
}
